import React, { FC, useState, useEffect } from 'react';
import './returnCardsModal.scss';
import Table from 'components/Elements/Table';
import Checkbox from 'components/Elements/Checkbox';
import type { SaveReturnCardsValues, TableDataObject } from 'pages/VisitorsOnSite/types';
import { useSelector } from 'react-redux';
import Tooltip from 'components/Elements/Tootltip';
import { CombinedStates } from 'core/types';
import { AccessCardObject } from 'core/securityDashboard/types';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';

interface ReturnCardsModalProps {
    onSave: (values: SaveReturnCardsValues) => void;
    selectedVisitorIds: string[];
}

interface ReturnTableDataObject {
    key: string;
    visitorName: string;
    assignedAccessCards: AccessCardObject[];
    assignedKeyReferences: AccessCardObject[];
}

const ReturnCardsModal: FC<ReturnCardsModalProps> = ({ onSave, selectedVisitorIds }) => {
    const [reasonForNoReturn, setReasonForNoReturn] = useState<string>('');
    const [checkedAccessCards, setCheckedAccessCards] = useState<string[]>([]);
    const [checkedKeyReferences, setCheckedKeyReferences] = useState<string[]>([]);

    const visitorsToday = useSelector((state: CombinedStates) => state.securityDashboard.visitorsToday);
    const selectedVisitors = visitorsToday.filter((visitor) => selectedVisitorIds.includes(visitor.visitRequestVisitorId.toString()));
    const visitorsWithCardOrKey = selectedVisitors.filter((visitor) => visitor.assignedAccessCards.length > 0 || visitor.assignedKeyReferences.length > 0);

    useEffect(() => {
        const getCheckedCardIds = visitorsWithCardOrKey.map((visitor) => visitor.assignedAccessCards.map((card) => card.recordId.toString())).flat();
        const getCheckedKeyIds = visitorsWithCardOrKey.map((visitor) => visitor.assignedKeyReferences.map((key) => key.recordId.toString())).flat();
        setCheckedAccessCards(getCheckedCardIds);
        setCheckedKeyReferences(getCheckedKeyIds);
    }, []);

    const handleAddAccessCardClick = (key: string) => {
        console.log('Add access card clicked', key);
    };

    const handleAddKeyReferenceClick = (key: string) => {
        console.log('Add key reference clicked', key);
    };

    const handleAccessCardCheckboxChange = (cardId: string, checked: boolean) => {
        if (checked) {
            setCheckedAccessCards(prev => [...prev, cardId]);
        } else {
            setCheckedAccessCards(prev => prev.filter(id => id !== cardId));
        }
    };

    const handleKeyReferenceCheckboxChange = (keyId: string, checked: boolean) => {
        if (checked) {
            setCheckedKeyReferences(prev => [...prev, keyId]);
        } else {
            setCheckedKeyReferences(prev => prev.filter(id => id !== keyId));
        }
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReasonForNoReturn(e.target.value);
    };

    const handleSaveClick = () => {
        const values = {
            cardOrKeyIds: [...checkedAccessCards, ...checkedKeyReferences],
            reasonForNoReturn,
        };
        onSave(values);
        setReasonForNoReturn('');
        setCheckedAccessCards([]);
        setCheckedKeyReferences([]);
    };

    const columns = [
        {
            title: 'Visitor',
            dataIndex: 'visitorName',
            key: 'visitorName',
            width: '33%',
        },
        {
            title: (
                <div className="header-with-checkbox">
                    {visitorsWithCardOrKey.some(visitor => visitor.assignedAccessCards.length > 0) && (
                        <Checkbox
                            checked={visitorsWithCardOrKey.every(visitor => visitor.assignedAccessCards.every(card => checkedAccessCards.includes(card.recordId.toString())))}
                            onClick={() => {
                                const allChecked = visitorsWithCardOrKey.every(visitor => visitor.assignedAccessCards.every(card => checkedAccessCards.includes(card.recordId.toString())));
                                visitorsWithCardOrKey.forEach(visitor => {
                                    visitor.assignedAccessCards.forEach(card => {
                                        handleAccessCardCheckboxChange(card.recordId.toString(), !allChecked);
                                    });
                                });
                            }} />
                    )}
                    <span>Temporary Access Card</span>
                </div>
            ),
            dataIndex: 'assignedAccessCards',
            key: 'assignedAccessCards',
            width: '35%',
            render: (assignedAccessCards: AccessCardObject[], record: TableDataObject) => {
                const accessCardDescriptions = assignedAccessCards.map((card) => card.description);
                const text = accessCardDescriptions.join(', ');
                if (text === '') {
                    const { key } = record;
                    return (
                        <div onClick={() => handleAddAccessCardClick(key)}>
                            <span className="add-label">Add</span>
                        </div>
                    );
                }
                return (
                    <div className="card-key-container">
                        <Checkbox
                            checked={assignedAccessCards.every(card => checkedAccessCards.includes(card.recordId.toString()))}
                            onClick={() => {
                                const allChecked = assignedAccessCards.every(card => checkedAccessCards.includes(card.recordId.toString()));
                                assignedAccessCards.forEach(card => {
                                    handleAccessCardCheckboxChange(card.recordId.toString(), !allChecked);
                                });
                            }} />
                        <p className="reference-text">{text}</p>
                    </div>
                );
            },
        },
        {
            title: (
                <div className="header-with-checkbox">
                    {visitorsWithCardOrKey.some(visitor => visitor.assignedKeyReferences.length > 0) && (
                        <Checkbox
                            checked={visitorsWithCardOrKey.every(visitor => visitor.assignedKeyReferences.every(key => checkedKeyReferences.includes(key.recordId.toString())))}
                            onClick={() => {
                                const allChecked = visitorsWithCardOrKey.every(visitor => visitor.assignedKeyReferences.every(key => checkedKeyReferences.includes(key.recordId.toString())));
                                visitorsWithCardOrKey.forEach(visitor => {
                                    visitor.assignedKeyReferences.forEach(key => {
                                        handleKeyReferenceCheckboxChange(key.recordId.toString(), !allChecked);
                                    });
                                });
                            }} />
                    )}
                    <span>Key Reference</span>
                </div>
            ),
            dataIndex: 'assignedKeyReferences',
            key: 'assignedKeyReferences',
            width: '33%',
            render: (assignedKeyReferences: AccessCardObject[], record: TableDataObject) => {
                const keyReferenceDescriptions = assignedKeyReferences.map((key) => key.description);
                const text = keyReferenceDescriptions.join(', ');
                if (text === '') {
                    const { key } = record;
                    return (
                        <div onClick={() => handleAddKeyReferenceClick(key)}>
                            <span className="add-label">Add</span>
                        </div>
                    );
                }
                return (
                    <div className="card-key-container">
                        <Checkbox
                            checked={assignedKeyReferences.every(key => checkedKeyReferences.includes(key.recordId.toString()))}
                            onClick={() => {
                                const allChecked = assignedKeyReferences.every(key => checkedKeyReferences.includes(key.recordId.toString()));
                                assignedKeyReferences.forEach(key => {
                                    handleKeyReferenceCheckboxChange(key.recordId.toString(), !allChecked);
                                });
                            }} />
                        <Tooltip title={text}>
                            <p className="reference-text">{text}</p>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    const tableData: ReturnTableDataObject[] = visitorsWithCardOrKey.map((visitor) => {
        const {
            visitRequestVisitorId,
            visitorName,
            assignedAccessCards,
            assignedKeyReferences,
        } = visitor;
        return {
            key: visitRequestVisitorId.toString(),
            visitorName,
            assignedAccessCards,
            assignedKeyReferences,
        };
    });

    return (
        <div className="return-cards-modal-container">
            <h1 className="heading">Returned Cards and Keys</h1>
            <p className="caption">
                Mark the Temporary Access Cards and Key References that have been returned as the visitors check out. If any have not been returned, please state the reason below.
            </p>
            <Table
                pagination={{
                    pageSize: 20,
                    currentPage: 1,
                    total: 20,
                    onChange: () => { console.log('hhh'); },
                }}
                columns={columns}
                data={tableData} />
            <div className="reason-container">
                <p className="caption">Reason for no return</p>
                <textarea
                    value={reasonForNoReturn}
                    onChange={handleReasonChange}
                    maxLength={MAX_LONG_TEXT_LENGTH}
                    placeholder="e.g. visitor will return to site tomorrow" />
            </div>
            <div className="button-wrapper">
                <button onClick={handleSaveClick} type="button">Save</button>
            </div>
        </div>
    );
};

export default ReturnCardsModal;
